import React from 'react';
import { TripMatchStatus, SalesStage, Partner } from '@marageti/z4-sdk/lib/travel';
import {
  ZAvatar,
  useContextMenu,
  ContextMenuButton,
  ContextMenu,
  ContextMenuItem,
  TripMatchChips,
  useApiClient,
  useFetchImmutable,
  CustomIcons,
} from '@marageti/z4-lib';
import { Box, Tooltip } from '@mui/material';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import MatchCard from './MatchCard';
import { isDeclinedOrCanceled } from '../../utils/trip-matches';
import { formatCountryCodes } from '../../utils/helpers';

const { getTripMatchStatusChip } = TripMatchChips;

type SalesStageIconMapping = {
  [key in SalesStage]: { icon: React.ReactNode, tooltip: string };
};

const salesStageIcons: SalesStageIconMapping = {
  [SalesStage.NeedsFollowUp]: { icon: <CustomIcons.Stage1Icon fontSize="small" />, tooltip: 'Needs Follow Up' }, // TODO: Deprecate
  [SalesStage.NeedsProposal]: { icon: <CustomIcons.Stage2Icon fontSize="small" />, tooltip: 'Needs Proposal' }, // TODO: Deprecate
  [SalesStage.ProposalSent]: { icon: <CustomIcons.Stage3Icon fontSize="small" />, tooltip: 'Proposal Sent' }, // TODO: Deprecate
  [SalesStage.FirstMessageSent]: { icon: <CustomIcons.SalesStage1Icon fontSize="small" />, tooltip: 'Sales Stage: First Message' },
  [SalesStage.Consultation]: { icon: <CustomIcons.SalesStage2Icon fontSize="small" />, tooltip: 'Sales Stage: Consultation' },
  [SalesStage.ItinerarySent]: { icon: <CustomIcons.SalesStage3Icon fontSize="small" />, tooltip: 'Sales Stage: Proposal Sent' },
  [SalesStage.ItineraryInReview]: { icon: <CustomIcons.SalesStage4Icon fontSize="small" />, tooltip: 'Sales Stage: Itinerary Review' },
};

const TripMatchCardTitle = ({ partnerName, salesStage }: {
  partnerName: string,
  salesStage: SalesStage,
}) => {
  const { icon, tooltip } = salesStageIcons[salesStage] || { icon: <CustomIcons.SalesStage0Icon fontSize="small" />, tooltip: 'Sales Stage: Undefined' };

  return (
    <Box gap={1} display="flex" alignItems="flex-start">
      {partnerName}
      <Tooltip title={tooltip}>
        <span>
          {icon}
        </span>
      </Tooltip>
    </Box>
  );
};

type TripMatchCardProps = {
  id: string,
  tripRequestId?: string,
  countries: string[],
  isAssigned: boolean,
  partnerId: string,
  partnerName: string,
  salesStage: SalesStage,
  status: TripMatchStatus,
  onCancelClick: (id:string) => void,
  onClickReactivate: (id: string) => void,
  onCancelSold: (id: string) => void,
  travelerId: string,
};

const TripMatchCard = ({
  id,
  tripRequestId,
  countries,
  isAssigned,
  partnerId,
  partnerName,
  salesStage,
  status,
  onCancelClick,
  onClickReactivate,
  onCancelSold,
  travelerId,
}: TripMatchCardProps) => {
  const [contextMenuAnchor, setContextMenuAnchor, contextMenuOpen] = useContextMenu();
  const buttonId = `${id}-context-button`;
  const menuId = `${id}-context-menu`;
  const isDeclinedOrCanceledMatch = isDeclinedOrCanceled(status);
  const isSold = status === TripMatchStatus.MatchSold;
  const canBeReactivated = status === TripMatchStatus.MatchPartnerDeclined && !isAssigned;
  const canBeCanceled = !isDeclinedOrCanceledMatch && !isSold;
  const showContextMenu = canBeReactivated || canBeCanceled || isSold;
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: partner } = useFetchImmutable<Partner>(apiClient.partnersClient.getPartner, [partnerId], !!partnerId);
  const formattedCountries = countryCodes && formatCountryCodes(countries, countryCodes);

  return (
    <Box
      sx={{
        display: 'block',
      }}
    >
      <MatchCard
        id={id}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            cursor: 'pointer',
          },
          backgroundColor: 'primary.contrastText',
        }}
        avatar={(
          <ZAvatar
            size={40}
            src={partner?.avatar}
            name={partnerName}
          />
        )}
        title={<TripMatchCardTitle partnerName={partnerName} salesStage={salesStage} />}
        subheader={countryCodes ? formattedCountries : '-'}
        actions={[
          <div key="status-chip" style={{ position: 'relative', top: -2 }}>
            {getTripMatchStatusChip(status, 'medium')}
          </div>,
          // eslint-disable-next-line react/jsx-no-useless-fragment
          !showContextMenu ? <Box width={24} /> : (
            <ContextMenuButton
              id={buttonId}
              open={contextMenuOpen}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setContextMenuAnchor(e.currentTarget);
              }}
              menuId={menuId}
            />
          ),
        ]}
        tripRequestId={tripRequestId}
        travelerId={travelerId}
      />
      <ContextMenu
        anchorEl={contextMenuAnchor}
        buttonId={buttonId}
        id={menuId}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setContextMenuAnchor(null);
        }}
        open={contextMenuOpen}
      >
        <ContextMenuItem
            // Needed since onClick is typed without the event arg
            // @ts-ignore
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (canBeReactivated) onClickReactivate(id);
            else if (isSold) onCancelSold(id);
            else onCancelClick(id);
            setContextMenuAnchor(null);
          }}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {canBeReactivated ? 'Reactivate' : isSold ? 'Cancel Sold Trip' : 'Cancel Match'}
        </ContextMenuItem>
      </ContextMenu>
    </Box>
  );
};

export default TripMatchCard;
